import React, { useState, useEffect } from 'react';
import api from '../axios';

function ConfigInput({ onSuccess }) {
  const [cryptoSymbols, setCryptoSymbols] = useState('');
  const [forexSymbols, setForexSymbols] = useState('');
  const [maxTrades, setMaxTrades] = useState(5);
  const [maxPending, setMaxPending] = useState(2);
  const [weekdayMaxTrades, setWeekdayMaxTrades] = useState(2);
  const [weekendMaxTrades, setWeekendMaxTrades] = useState(2);
  const [dailyProfit, setDailyProfit] = useState(100.0);
  const [dailyLoss, setDailyLoss] = useState(50.0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingConfig, setIsFetchingConfig] = useState(true);
  const embeddedInModal = typeof onSuccess === 'function';

  const applyConfig = (config) => {
    setCryptoSymbols(Array.isArray(config.crypto_symbols) ? config.crypto_symbols.join(', ') : (config.crypto_symbols || ''));
    setForexSymbols(Array.isArray(config.forex_symbols) ? config.forex_symbols.join(', ') : (config.forex_symbols || ''));
    setMaxTrades(config.max_trades ?? 5);
    setMaxPending(config.max_pending ?? 2);
    setWeekdayMaxTrades(config.weekday_max_trades ?? 2);
    setWeekendMaxTrades(config.weekend_max_trades ?? 2);
    setDailyProfit(config.daily_profit ?? 100.0);
    setDailyLoss(config.daily_loss ?? 50.0);
  };

  // Load initial configuration if available
  useEffect(() => {
    const loadConfig = async () => {
      setIsFetchingConfig(true);
      try {
        const response = await api.get('/api/config');
        if (response.status >= 400 || !response.data) {
          throw new Error(response.data?.detail || response.data?.message || 'Failed to load configuration');
        }

        applyConfig(response.data);
      } catch (err) {
        console.error('Error loading config:', err);
        setError('Failed to load saved configuration. Showing fallback values.');
        applyConfig({});
      } finally {
        setIsFetchingConfig(false);
      }
    };
    loadConfig();
  }, []);

  const handleCryptoInput = (e) => {
    setCryptoSymbols(e.target.value);
  };

  const handleForexInput = (e) => {
    setForexSymbols(e.target.value);
  };

  const handleSave = async (e) => {
    if (e) {
      e.preventDefault();
    }
    setError('');
    setSuccess('');
    setIsLoading(true);

    const cryptoArray = cryptoSymbols.split(',').map(s => s.trim()).filter(Boolean);
    const forexArray = forexSymbols.split(',').map(s => s.trim()).filter(Boolean);
    const allSymbols = [...cryptoArray, ...forexArray];

    if (!allSymbols.length || !maxTrades || !maxPending || !weekdayMaxTrades || !weekendMaxTrades || !dailyProfit || !dailyLoss) {
      setError('Please fill in all fields. At least one symbol is required.');
      setIsLoading(false);
      return;
    }

    if (maxTrades <= 0) {
      setError('Max trades must be a positive number.');
      setIsLoading(false);
      return;
    }

    if (maxPending <= 0) {
      setError('Max pending orders must be a positive number.');
      setIsLoading(false);
      return;
    }

    if (weekdayMaxTrades <= 0) {
      setError('Weekday max trades must be a positive number.');
      setIsLoading(false);
      return;
    }

    if (weekendMaxTrades <= 0) {
      setError('Weekend max trades must be a positive number.');
      setIsLoading(false);
      return;
    }

    if (dailyProfit <= 0) {
      setError('Daily profit must be a positive number.');
      setIsLoading(false);
      return;
    }

    if (dailyLoss <= 0) {
      setError('Daily loss must be a positive number.');
      setIsLoading(false);
      return;
    }

    const symbolRegex = /^[A-Z0-9]+[a-z]?$/;
    const invalidSymbols = allSymbols.filter(s => !symbolRegex.test(s));
    if (invalidSymbols.length > 0) {
      setError(`Invalid symbols: ${invalidSymbols.join(', ')}. Symbols must be uppercase letters and numbers with an optional lowercase suffix (e.g., BTCUSDz).`);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.post('/api/update_config', {
        symbols: allSymbols,
        crypto_symbols: cryptoArray,
        forex_symbols: forexArray,
        max_trades: parseInt(maxTrades),
        max_pending: parseInt(maxPending),
        weekday_max_trades: parseInt(weekdayMaxTrades),
        weekend_max_trades: parseInt(weekendMaxTrades),
        daily_profit: parseFloat(dailyProfit),
        daily_loss: parseFloat(dailyLoss)
      });
      setSuccess(response.data.status);
      setError('');
      if (typeof onSuccess === 'function') {
        setTimeout(() => onSuccess(), 1200);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || `Failed to save configuration: ${err.message}. Ensure the backend is running on port 5000.`;
      setError(errorMessage);
      setSuccess('');
      console.error('Error saving configuration:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartTrading = async () => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    const cryptoArray = cryptoSymbols.split(',').map(s => s.trim()).filter(Boolean);
    const forexArray = forexSymbols.split(',').map(s => s.trim()).filter(Boolean);
    const symbolsArray = [...cryptoArray, ...forexArray];

    if (!symbolsArray.length) {
      setError('No symbols configured. Please save symbols first.');
      setIsLoading(false);
      return;
    }

    const symbolRegex = /^[A-Z0-9]+[a-z]?$/;
    const invalidSymbols = symbolsArray.filter(s => !symbolRegex.test(s));
    if (invalidSymbols.length > 0) {
      setError(`Invalid symbols: ${invalidSymbols.join(', ')}. Symbols must be uppercase letters and numbers with an optional lowercase suffix (e.g., BTCUSDz).`);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.post('/api/start_trading');
      setSuccess(`Trading started for: ${symbolsArray.join(', ')} - Status: ${response.data.status}`);
    } catch (err) {
      let errorMessage = 'Failed to start trading: ';
      if (err.response) {
        errorMessage += `Server responded with status ${err.response.status}: ${err.response.data?.error || 'Unknown error'}`;
      } else if (err.request) {
        errorMessage += 'No response received from the server. Ensure the backend is running on port 5000.';
      } else {
        errorMessage += err.message;
      }
      setError(errorMessage);
      setSuccess('');
      console.error('Error starting trading:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopTrading = async () => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    const cryptoArray = cryptoSymbols.split(',').map(s => s.trim()).filter(Boolean);
    const forexArray = forexSymbols.split(',').map(s => s.trim()).filter(Boolean);
    const symbolsArray = [...cryptoArray, ...forexArray];

    if (!symbolsArray.length) {
      setError('No symbols configured. Please save symbols first.');
      setIsLoading(false);
      return;
    }

    try {
        const response = await api.post('/api/stop_trading', {
          close_positions: false
        });
      setSuccess(`Trading stopped for: ${symbolsArray.join(', ')} - Status: ${response.data.status}`);
    } catch (err) {
      let errorMessage = 'Failed to stop trading: ';
      if (err.response) {
        errorMessage += `Server responded with status ${err.response.status}: ${err.response.data?.error || 'Unknown error'}`;
      } else if (err.request) {
        errorMessage += 'No response received from the server. Ensure the backend is running on port 5000.';
      } else {
        errorMessage += err.message;
      }
      setError(errorMessage);
      setSuccess('');
      console.error('Error stopping trading:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const panel = (
    <div className="w-full rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,rgba(13,23,34,0.98),rgba(8,14,22,0.98))] p-6 shadow-[0_36px_90px_rgba(0,0,0,0.45)] sm:p-7">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-white/10 pb-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-200/70">Trading rules</p>
          <h1 className="mt-2 text-2xl font-black tracking-tight text-white sm:text-3xl">Configuration settings</h1>
          <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-400">Control the symbol universe, risk thresholds, and trade limits from the same visual system as your account area.</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-slate-300">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">State</div>
          <div className="mt-2 font-semibold text-white">{isFetchingConfig ? 'Loading saved settings' : isLoading ? 'Saving changes' : 'Ready to update'}</div>
        </div>
      </div>

      {isFetchingConfig && (
        <div className="mb-5 flex items-center gap-3 rounded-2xl border border-cyan-300/20 bg-cyan-300/10 px-4 py-4 text-sm text-cyan-100">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-cyan-100/30 border-t-cyan-100" />
          <p>Loading your saved configuration...</p>
        </div>
      )}

      {error && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-rose-300/20 bg-rose-300/10 px-4 py-4 text-sm text-rose-100">
          <svg className="mt-0.5 h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-emerald-300/20 bg-emerald-300/10 px-4 py-4 text-sm text-emerald-100">
          <svg className="mt-0.5 h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <p>{success}</p>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-5">
        <fieldset disabled={isFetchingConfig || isLoading} className="space-y-5 disabled:opacity-70">
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200" htmlFor="cryptoSymbols">
              Crypto symbols
            </label>
            <input
              type="text"
              id="cryptoSymbols"
              value={cryptoSymbols}
              onChange={handleCryptoInput}
              className="w-full rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40 focus:bg-slate-950/70"
              placeholder="e.g. BTCUSD, ETHUSD"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200" htmlFor="forexSymbols">
              Forex symbols
            </label>
            <input
              type="text"
              id="forexSymbols"
              value={forexSymbols}
              onChange={handleForexInput}
              className="w-full rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40 focus:bg-slate-950/70"
              placeholder="e.g. EURUSD, GBPUSD"
            />
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200" htmlFor="maxTrades">
              Maximum trades
            </label>
            <input
              type="number"
              id="maxTrades"
              value={maxTrades}
              onChange={(e) => setMaxTrades(Number(e.target.value))}
              className="w-full rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40 focus:bg-slate-950/70"
              min="1"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200" htmlFor="maxPending">
              Max pending orders
            </label>
            <input
              type="number"
              id="maxPending"
              value={maxPending}
              onChange={(e) => setMaxPending(Number(e.target.value))}
              className="w-full rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40 focus:bg-slate-950/70"
              min="1"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200" htmlFor="weekdayMaxTrades">
              Weekday max trades
            </label>
            <input
              type="number"
              id="weekdayMaxTrades"
              value={weekdayMaxTrades}
              onChange={(e) => setWeekdayMaxTrades(Number(e.target.value))}
              className="w-full rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40 focus:bg-slate-950/70"
              min="1"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200" htmlFor="weekendMaxTrades">
              Weekend max trades
            </label>
            <input
              type="number"
              id="weekendMaxTrades"
              value={weekendMaxTrades}
              onChange={(e) => setWeekendMaxTrades(Number(e.target.value))}
              className="w-full rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40 focus:bg-slate-950/70"
              min="1"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200" htmlFor="dailyProfit">
              Daily profit target ($)
            </label>
            <input
              type="number"
              id="dailyProfit"
              value={dailyProfit}
              onChange={(e) => setDailyProfit(Number(e.target.value))}
              className="w-full rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40 focus:bg-slate-950/70"
              min="0.01"
              step="0.01"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200" htmlFor="dailyLoss">
              Daily loss limit ($)
            </label>
            <input
              type="number"
              id="dailyLoss"
              value={dailyLoss}
              onChange={(e) => setDailyLoss(Number(e.target.value))}
              className="w-full rounded-2xl border border-white/10 bg-slate-950/50 px-4 py-3 text-white placeholder-slate-500 outline-none transition focus:border-cyan-300/40 focus:bg-slate-950/70"
              min="0.01"
              step="0.01"
            />
          </div>
        </div>

        <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.04] p-4 text-sm text-slate-300">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Symbol format</div>
          <p className="mt-2 leading-7">Use uppercase letters and numbers with an optional lowercase suffix, for example BTCUSDz.</p>
        </div>

        <div className={`border-t border-white/10 pt-5 ${embeddedInModal ? 'space-y-3' : 'grid gap-3 sm:grid-cols-3'}`}>
          <button
            type="submit"
            disabled={isFetchingConfig || isLoading}
            className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-emerald-300 to-cyan-300 px-5 py-3 text-sm font-bold text-slate-950 transition disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isFetchingConfig ? 'Loading...' : isLoading ? 'Saving...' : 'Save configuration'}
          </button>

          {embeddedInModal ? (
            <p className="text-sm text-slate-400">Start and stop trading from your account area after saving this configuration.</p>
          ) : (
            <>
              <button
                type="button"
                onClick={handleStartTrading}
                className="inline-flex items-center justify-center rounded-2xl border border-emerald-300/20 bg-emerald-300/10 px-5 py-3 text-sm font-semibold text-emerald-100 transition hover:bg-emerald-300/15 disabled:cursor-not-allowed disabled:opacity-70"
                disabled={isLoading}
              >
                {isLoading ? 'Starting...' : 'Start Trading'}
              </button>

              <button
                type="button"
                onClick={handleStopTrading}
                className="inline-flex items-center justify-center rounded-2xl border border-rose-300/20 bg-rose-300/10 px-5 py-3 text-sm font-semibold text-rose-100 transition hover:bg-rose-300/15 disabled:cursor-not-allowed disabled:opacity-70"
                disabled={isLoading}
              >
                {isLoading ? 'Stopping...' : 'Stop Trading'}
              </button>
            </>
          )}
        </div>
        </fieldset>
      </form>
    </div>
  );

  if (embeddedInModal) {
    return panel;
  }

  return (
    <div className="min-h-screen bg-[#07111a] px-4 py-10 text-white">
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(27,186,123,0.14),_transparent_30%),radial-gradient(circle_at_80%_20%,_rgba(45,122,255,0.12),_transparent_28%),linear-gradient(180deg,_#09131d_0%,_#05080c_100%)]" />
      <div className="mx-auto max-w-4xl">{panel}</div>
    </div>
  );
}

export default ConfigInput;